import type { ComponentType } from "react";
import type { RouteObject } from "react-router";
import { NotFoundPage, RouteErrorPage, ScreenErrorPage } from "../../app/ErrorPage";
import { Loading } from "../../shared/ui/Loading";
import type * as PlatformPages from "./platformPages";
import { RequirePlatformManage } from "./PlatformGate";
import { PlatformLayout } from "./PlatformLayout";
import { platformPaths } from "./platformNavigation";

/**
 * Loads a page of the operator area on first use, so the application of the tenants never loads it.
 *
 * @param load - Dynamic import of the module.
 * @param pick - Selects the component from the module.
 * @returns The lazy route definition.
 */
function lazyPage<TModule>(
  load: () => Promise<TModule>,
  pick: (module: TModule) => ComponentType,
): NonNullable<RouteObject["lazy"]> {
  const lazy = async (): Promise<{ Component: ComponentType }> => {
    const module = await load();
    const route = { Component: pick(module) };

    return route;
  };

  return lazy;
}

/**
 * Creates the route of the operator area (F-69) below the sign-in. `PlatformGate` lets only
 * operator staff in; inside the shell the pages share one error element, so a failing page keeps the
 * app bar and the navigation. A further page adds one child here and one entry in
 * `platformNavigation.ts`.
 *
 * @returns The route.
 */
export function createPlatformRoute(): RouteObject {
  const pages = (): Promise<typeof PlatformPages> => import("./platformPages");
  const route: RouteObject = {
    path: platformPaths.root,
    element: <PlatformLayout />,
    errorElement: <RouteErrorPage />,
    hydrateFallbackElement: <Loading />,
    children: [
      {
        errorElement: <ScreenErrorPage />,
        children: [
          { index: true, lazy: lazyPage(pages, (m) => m.PlatformOverviewPage) },
          { path: "tenants", lazy: lazyPage(pages, (m) => m.PlatformTenantsPage) },
          { path: "tenants/:tenantId", lazy: lazyPage(pages, (m) => m.PlatformTenantPage) },
          {
            element: <RequirePlatformManage />,
            children: [
              { path: "onboarding", lazy: lazyPage(pages, (m) => m.PlatformOnboardingPage) },
              {
                path: "security-incidents",
                lazy: lazyPage(pages, (m) => m.PlatformSecurityIncidentPage),
              },
              { path: "databases", lazy: lazyPage(pages, (m) => m.PlatformDatabasesPage) },
            ],
          },
          {
            path: "onboardings/:tenantId",
            lazy: lazyPage(pages, (m) => m.PlatformOnboardingStatusPage),
          },
          { path: "signups", lazy: lazyPage(pages, (m) => m.PlatformSignupsPage) },
          { path: "billing", lazy: lazyPage(pages, (m) => m.PlatformBillingPage) },
          {
            path: "legal-documents",
            lazy: lazyPage(pages, (m) => m.PlatformLegalDocumentsPage),
          },
          { path: "subprocessors", lazy: lazyPage(pages, (m) => m.PlatformSubprocessorsPage) },
          { path: "contract-end", lazy: lazyPage(pages, (m) => m.PlatformContractEndPage) },
          { path: "audit-log", lazy: lazyPage(pages, (m) => m.PlatformAuditLogPage) },
          { path: "plans", lazy: lazyPage(pages, (m) => m.PlatformPlansPage) },
          { path: "system-status", lazy: lazyPage(pages, (m) => m.PlatformSystemStatusPage) },
          {
            path: "staff-accounts",
            lazy: lazyPage(pages, (m) => m.PlatformStaffAccountsPage),
          },
          { path: "account", lazy: lazyPage(pages, (m) => m.PlatformAccountPage) },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  };

  return route;
}
